import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, IconButton, Checkbox, TablePagination,
  Button, Avatar, CircularProgress, Alert, Menu, MenuItem,
  Snackbar, Tooltip, alpha, FormControl, InputLabel, Select, Divider,
  useTheme, useMediaQuery
} from '@mui/material';
import { MoreVertical, Plus, Building2, Trash2, Edit, CheckCircle2, Package } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBusinesses, fetchCategories, fetchSubCategories, fetchCountries, fetchStates, fetchCities, deleteBusiness, bulkDeleteBusinesses } from '../redux/slices/businessSlice';
import { createAdminBrand } from '../redux/slices/catalogSlice';
import BusinessModal from '../components/BusinessModal';
import BusinessProductsModal from '../components/BusinessProductsModal';
import PageHeader from '../components/common/PageHeader';
import FilterBar from '../components/common/FilterBar';
import { DeleteConfirmDialog, FilterDrawer, ImagePreviewDialog } from '../components/common/modal';
import EmptyState from '../components/common/EmptyState';
import { getBusinessLogoImageUrl } from '../utils/helpers';
import { formatDateTime } from '../utils/formatDate';
import axios from '../utils/axios';
import ExportTableButton from '../components/common/ExportTableButton';

const BusinessesView = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { businesses, categories, subCategories, countries, states, cities, totalBusinesses, isLoading, error } = useSelector((state) => state.business);

  // Search & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const activeFiltersCount = [typeFilter, categoryFilter, stateFilter, cityFilter].filter(Boolean).length;

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Modal & Row actions
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [actionBusiness, setActionBusiness] = useState(null);

  // Connected Products Modal
  const [productsModalOpen, setProductsModalOpen] = useState(false);
  const [selectedBusinessForProducts, setSelectedBusinessForProducts] = useState(null);

  const handleOpenProductsModal = (business) => {
    setSelectedBusinessForProducts(business);
    setProductsModalOpen(true);
  };

  // Delete Confirm Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Image Preview Modal
  const [previewImage, setPreviewImage] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadBusinesses = useCallback(() => {
    dispatch(fetchBusinesses({
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      type: typeFilter,
      category: categoryFilter,
      state: stateFilter,
      city: cityFilter
    }));
  }, [dispatch, page, rowsPerPage, debouncedSearch, typeFilter, categoryFilter, stateFilter, cityFilter]);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchSubCategories());
    dispatch(fetchCountries());
    dispatch(fetchStates());
    dispatch(fetchCities());
  }, [dispatch]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = businesses.map((n) => n._id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedIds.slice(0, selectedIndex),
        selectedIds.slice(selectedIndex + 1),
      );
    }
    setSelectedIds(newSelected);
  };

  const handleMenuOpen = (event, business) => {
    setMenuAnchorEl(event.currentTarget);
    setActionBusiness(business);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActionBusiness(null);
  };

  const handleEditClick = () => {
    setEditingBusiness(actionBusiness);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    // Only close dropdown menu anchor — do NOT clear actionBusiness yet so confirmDelete can use it
    setMenuAnchorEl(null);
    setIsBulkDelete(false);
    setDeleteDialogOpen(true);
  };

  const handleBulkDeleteClick = () => {
    setIsBulkDelete(true);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      if (isBulkDelete) {
        const result = await dispatch(bulkDeleteBusinesses(selectedIds));
        if (result.meta.requestStatus === 'fulfilled') {
          setSelectedIds([]);
          setSnackbar({ open: true, message: 'Selected businesses deleted successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.payload || 'Failed to delete businesses', severity: 'error' });
        }
      } else if (actionBusiness) {
        const result = await dispatch(deleteBusiness(actionBusiness._id));
        if (result.meta.requestStatus === 'fulfilled') {
          setSnackbar({ open: true, message: 'Business deleted successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.payload || 'Failed to delete business', severity: 'error' });
        }
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An unexpected error occurred', severity: 'error' });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setActionBusiness(null);
      loadBusinesses();
    }
  };

  const handleAddBrandForBusiness = async (b) => {
    try {
      const categoryDoc = categories?.find(c => c.category_name === b.business_category);
      if (!categoryDoc) {
        setSnackbar({ open: true, message: 'Category not found for this business', severity: 'error' });
        return;
      }
      const result = await dispatch(createAdminBrand({
        brand_name: b.business_name,
        category_id: categoryDoc._id
      }));
      if (result.meta.requestStatus === 'fulfilled') {
        setSnackbar({ open: true, message: `Brand "${b.business_name}" created successfully!`, severity: 'success' });
        loadBusinesses();
      } else {
        setSnackbar({ open: true, message: result.payload || 'Failed to create brand', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to create brand', severity: 'error' });
    }
  };

  const norm = (val) => String(val || '').trim().toLowerCase();
  const selectedFilterStateObj = Array.isArray(states)
    ? states.find(s => norm(s.state_name) === norm(stateFilter) || String(s._id) === String(stateFilter))
    : null;

  const filterDrawerCities = stateFilter && Array.isArray(cities)
    ? Array.from(
      new Map(
        cities
          .filter(c => {
            if (norm(c.state?.state_name) === norm(stateFilter)) return true;
            if (typeof c.state === 'string' && norm(c.state) === norm(stateFilter)) return true;
            if (norm(c.state_name) === norm(stateFilter)) return true;
            if (selectedFilterStateObj && (
              String(c.state_id) === String(selectedFilterStateObj._id) ||
              String(c.state_id?._id) === String(selectedFilterStateObj._id) ||
              String(c.state?._id) === String(selectedFilterStateObj._id)
            )) return true;
            return false;
          })
          .map(c => [norm(c.city_name), c])
      ).values()
    ).sort((a, b) => (a.city_name || '').localeCompare(b.city_name || ''))
    : (Array.isArray(cities) ? cities : []);

  const isSelected = (id) => selectedIds.indexOf(id) !== -1;

  return (
    <Box>
      <PageHeader
        title="Businesses"
        subtitle="Manage registered businesses, store profiles, brand links and locations"
        count={totalBusinesses}
        countLabel="businesses"
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => { setEditingBusiness(null); setIsModalOpen(true); }}
          >
            New business
          </Button>
        }
      />

      <Card sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        {isLoading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6), zIndex: 10, backdropFilter: 'blur(2px)'
          }}>
            <CircularProgress thickness={4} size={36} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {/* Modernized FilterBar */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={(v) => setSearchTerm(v.trimStart())}
          searchPlaceholder="Search by business name or owner name..."
          selectedCount={selectedIds.length}
          onBulkDelete={handleBulkDeleteClick}
          onOpenFilter={() => setIsFilterOpen(true)}
          filterCount={activeFiltersCount}
          hasActiveFilters={Boolean(typeFilter || categoryFilter || stateFilter || cityFilter || searchTerm)}
          onResetFilters={() => {
            setTypeFilter('');
            setCategoryFilter('');
            setStateFilter('');
            setCityFilter('');
            setSearchTerm('');
            setPage(0);
          }}
        >
          <ExportTableButton
            data={businesses}
            total={totalBusinesses}
            onExportAll={async () => {
              const res = await axios.get('/admin/business', {
                params: {
                  all: true,
                  search: debouncedSearch || undefined,
                  category: categoryFilter || undefined,
                  type: typeFilter || undefined,
                  state: stateFilter || undefined,
                  city: cityFilter || undefined,
                },
              });
              return res.data.businesses || [];
            }}
            columns={[
              { label: 'Business Name', key: 'business_name' },
              { label: 'Category', key: 'business_category' },
              { label: 'Type', key: 'business_type' },
              { label: 'City', value: (row) => row.city || row.business_city || '' },
              { label: 'State', value: (row) => row.state || row.business_state || '' },
              { label: 'Country', value: (row) => row.country || row.business_country || '' },
              { label: 'Owner Name', value: (row) => `${row.owner?.first_name || row.user_id?.first_name || ''} ${row.owner?.last_name || row.user_id?.last_name || ''}`.trim() || '—' },
              { label: 'Owner Phone', value: (row) => row.owner?.phone || row.user_id?.phone || '' },
              { label: 'Products Count', key: 'productsCount' },
              { label: 'Is Brand', value: (row) => row.isBrand ? 'Yes' : 'No' },
              { label: 'Created', key: 'createdAt' },
            ]}
            filename="businesses"
          />
        </FilterBar>

        {error && !isLoading ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead sx={{ backgroundColor: 'background.neutral' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={selectedIds.length > 0 && selectedIds.length < (businesses?.length || 0)}
                      checked={businesses?.length > 0 && selectedIds.length === businesses.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Business Name</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Brand</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Products</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Location</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Owner</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Created</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {businesses && businesses.length > 0 ? businesses.map((business) => {
                  const isItemSelected = isSelected(business._id);
                  return (
                    <TableRow key={business._id} hover selected={isItemSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, business._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={getBusinessLogoImageUrl(business.business_logo)}
                            variant="rounded"
                            onClick={() => {
                              if (business.business_logo) {
                                setPreviewImage({
                                  url: getBusinessLogoImageUrl(business.business_logo),
                                  title: business.business_name
                                });
                              }
                            }}
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: 'background.neutral',
                              cursor: business.business_logo ? 'pointer' : 'default',
                              transition: 'transform 0.15s ease',
                              '&:hover': business.business_logo ? { transform: 'scale(1.05)' } : {}
                            }}
                          >
                            <Building2 size={24} color="#919EAB" />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{business.business_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{business.business_category}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={business.isBrand ? 'Already exists as a brand name' : 'Click to create as brand'}>
                          {business.isBrand ? (
                            <Chip
                              icon={<CheckCircle2 size={16} />}
                              label="Brand"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12),
                                color: 'success.dark',
                                borderRadius: 1,
                                '& .MuiChip-icon': { color: 'success.main' }
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<Plus size={14} />}
                              label="Add Brand"
                              size="small"
                              onClick={() => handleAddBrandForBusiness(business)}
                              sx={{
                                fontWeight: 700,
                                backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                                color: 'info.dark',
                                borderRadius: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.25),
                                  transform: 'scale(1.04)'
                                },
                                '& .MuiChip-icon': { color: 'info.main' }
                              }}
                            />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={business.productsCount > 0 ? "Click to view connected products" : "No products uploaded yet — click to view"}>
                          <Chip
                            icon={<Package size={14} />}
                            label={`${business.productsCount ?? 0} ${business.productsCount === 1 ? 'Product' : 'Products'}`}
                            size="small"
                            clickable
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenProductsModal(business);
                            }}
                            sx={{
                              fontWeight: 700,
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              height: 24,
                              borderRadius: 1.5,
                              backgroundColor: (theme) =>
                                (business.productsCount > 0)
                                  ? alpha(theme.palette.primary.main, 0.12)
                                  : alpha(theme.palette.text.disabled, 0.08),
                              color: (theme) =>
                                (business.productsCount > 0)
                                  ? theme.palette.primary.dark
                                  : theme.palette.text.secondary,
                              borderColor: (theme) =>
                                (business.productsCount > 0)
                                  ? alpha(theme.palette.primary.main, 0.3)
                                  : 'transparent',
                              borderWidth: 1,
                              borderStyle: 'solid',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: (theme) =>
                                  (business.productsCount > 0)
                                    ? alpha(theme.palette.primary.main, 0.22)
                                    : alpha(theme.palette.text.disabled, 0.16),
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                              },
                              '& .MuiChip-icon': {
                                color: (business.productsCount > 0) ? 'primary.main' : 'text.disabled',
                              }
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{business.business_type}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{business.city}</Typography>
                        <Typography variant="caption" color="text.secondary">{business.state}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{business.owner ? `${business.owner.first_name} ${business.owner.last_name}` : 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap>{formatDateTime(business.createdAt || business.created_at)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton aria-label="Row actions" size="small" onClick={(e) => handleMenuOpen(e, business)}>
                          <MoreVertical size={20} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }) : !isLoading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ p: 0 }}>
                      <EmptyState
                        icon={Building2}
                        title="No businesses found"
                        description="Try adjusting your filters or search query to find registered businesses."
                        actionLabel="Add Business"
                        onAction={() => { setEditingBusiness(null); setIsModalOpen(true); }}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 } }}>
          <TablePagination
            component="div"
            count={totalBusinesses || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Row Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { width: 190, p: 0.5 }
          }
        }}
      >
        <MenuItem
          onClick={() => {
            handleOpenProductsModal(actionBusiness);
            handleMenuClose();
          }}
          sx={{ fontSize: '0.875rem', gap: 1.25, borderRadius: 1 }}
        >
          <Package size={16} /> Products ({actionBusiness?.productsCount ?? 0})
        </MenuItem>
        <MenuItem onClick={handleEditClick} sx={{ fontSize: '0.875rem', gap: 1.25, borderRadius: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1.25, borderRadius: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {/* Create / Edit Modal (With Image Uploads) */}
      <BusinessModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        business={editingBusiness}
        categories={categories}
        subCategories={subCategories}
        countries={countries}
        states={states}
        cities={cities}
        onSuccess={() => {
          loadBusinesses();
          setSnackbar({ open: true, message: `Business ${editingBusiness ? 'updated' : 'created'} successfully!`, severity: 'success' });
        }}
      />

      {/* Connected Products Modal */}
      <BusinessProductsModal
        open={productsModalOpen}
        onClose={() => setProductsModalOpen(false)}
        business={selectedBusinessForProducts}
      />

      {/* Standard Filter Drawer */}
      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="Filter Businesses"
        activeCount={activeFiltersCount}
        width={420}
        onReset={() => {
          setTypeFilter('');
          setCategoryFilter('');
          setStateFilter('');
          setCityFilter('');
          setPage(0);
          setIsFilterOpen(false);
        }}
        onApply={() => setIsFilterOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Business Type Filter */}
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', letterSpacing: 0.5, mb: 1, display: 'block' }}>
              BUSINESS TYPE
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={typeFilter}
                displayEmpty
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
                sx={{ borderRadius: 1.5 }}
              >
                <MenuItem value="">All Types</MenuItem>
                {['Wholesaler', 'Retailer', 'Manufacturer', 'Reseller', 'Broker', 'Service Provider'].map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Category Filter */}
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', letterSpacing: 0.5, mb: 1, display: 'block' }}>
              CATEGORY
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={categoryFilter}
                displayEmpty
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
                sx={{ borderRadius: 1.5 }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories?.map((cat) => (
                  <MenuItem key={cat._id} value={cat.category_name}>{cat.category_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* Region Filter */}
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
              LOCATION
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.875rem' }}>State</InputLabel>
                <Select
                  value={stateFilter}
                  label="State"
                  onChange={(e) => { setStateFilter(e.target.value); setCityFilter(''); setPage(0); }}
                  sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value="">All States</MenuItem>
                  {states?.map((s) => (
                    <MenuItem key={s._id} value={s.state_name}>{s.state_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={!stateFilter}>
                <InputLabel sx={{ fontSize: '0.875rem' }}>City</InputLabel>
                <Select
                  value={cityFilter}
                  label="City"
                  onChange={(e) => { setCityFilter(e.target.value); setPage(0); }}
                  sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value="">All Cities</MenuItem>
                  {filterDrawerCities?.map((c) => (
                    <MenuItem key={c._id} value={c.city_name}>{c.city_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      </FilterDrawer>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { if (!isDeleting) { setDeleteDialogOpen(false); setActionBusiness(null); } }}
        onDelete={confirmDelete}
        onConfirm={confirmDelete}
        loading={isDeleting}
        itemName={isBulkDelete ? `${selectedIds.length} businesses` : actionBusiness?.business_name}
        itemType={isBulkDelete ? 'Selected Businesses' : 'Business'}
        description={
          isBulkDelete
            ? `Are you sure you want to permanently delete these ${selectedIds.length} businesses? Associated catalogs and records may be affected.`
            : `Are you sure you want to permanently delete "${actionBusiness?.business_name || 'this business'}"? This action cannot be undone.`
        }
      />

      {/* Image Preview Lightbox */}
      <ImagePreviewDialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        src={previewImage?.url}
        title={previewImage?.title || 'Business Logo'}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2, boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessesView;
